import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '../../../core/button';
import { Input } from '../../../core/input';
import { Popover, PopoverContent, PopoverTrigger } from '../../../core/popover';
import { ExerciseType } from '@org/model';
import { cn } from '../../../../utils';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import type { ContentDisplayVariant } from '../../hooks/content-renderer-types';
import type { StudentExerciseContent } from '../../hooks/use-class-blocks';
import type {
  ExerciseFeedbackMessage,
  ExerciseSubmitHandler,
} from '../../hooks/content-renderer-types';
import { ExerciseFeedbackList } from '../../components/shared/exercise-feedback-list';
import { ContentDescription } from '../../components/shared/content-description';
import { renderInlineMarkdown } from '../../../custom/markdown';

interface FillInBlankExerciseProps {
  content: StudentExerciseContent;
  onExerciseSubmit?: ExerciseSubmitHandler;
  feedback?: ExerciseFeedbackMessage[];
  variant?: ContentDisplayVariant;
  isReadOnly?: boolean;
}

const BLANK_MARKER = '___';

export function FillInBlankExercise({
  content,
  onExerciseSubmit,
  feedback,
  variant = 'card',
  isReadOnly = false,
}: FillInBlankExerciseProps) {
  const sentences = content.sentences ?? [];
  const lastSubmission = useMemo(() => {
    const submission = content.lastSubmission;
    if (submission?.type === ExerciseType.FillInBlank) {
      return submission;
    }
    return null;
  }, [content.lastSubmission]);
  const lastEvaluationDetails = useMemo(() => {
    const evaluation = (content.lastEvaluation as any) ?? null;
    const autoDetails = (evaluation?.auto as any)?.details ?? evaluation?.details;
    return autoDetails?.kind === 'FillInBlank' ? autoDetails : null;
  }, [content.lastEvaluation]);

  const blankSignature = useMemo(
    () => JSON.stringify(sentences.map((sentence) => Math.max(1, sentence.blanks?.length ?? 0))),
    [sentences]
  );

  const normalizeAnswers = useCallback(
    (rawAnswers: Array<string | string[] | undefined>): string[][] =>
      sentences.map((sentence, sentenceIndex) => {
        const blanks =
          (sentence.blanks?.length
            ? sentence.blanks
            : [{ answer: (sentence as any)?.answer ?? '' }]) ?? [];
        const provided = rawAnswers?.[sentenceIndex];
        const providedArray = Array.isArray(provided)
          ? provided
          : typeof provided === 'string'
            ? [provided]
            : [];
        return blanks.map((_, blankIndex) => {
          const value = providedArray?.[blankIndex];
          return typeof value === 'string' ? value : '';
        });
      }),
    [sentences]
  );

  const normalizedLastAnswers = useMemo(() => {
    const rawAnswers = lastSubmission?.answers ?? [];
    return normalizeAnswers(rawAnswers as Array<string | string[] | undefined>);
  }, [lastSubmission, normalizeAnswers]);

  const [answers, setAnswers] = useState<string[][]>(() => normalizedLastAnswers);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [answerEvaluations, setAnswerEvaluations] = useState<
    Array<
      Array<{
        isCorrect: boolean;
        expected: string;
        provided: string;
        correction?: string;
        grade?: 'perfect' | 'near' | 'partial' | 'wrong';
        reason?: string;
      }>
    >
  >([]);

  const computeEvaluations = useCallback(
    (submittedAnswers: string[][]) =>
      sentences.map((sentence, sentenceIndex) => {
        const blanks = (sentence.blanks?.length ? sentence.blanks : [{ answer: (sentence as any)?.answer ?? '', correction: (sentence as any)?.correction }]) ?? [];
        const provided = submittedAnswers?.[sentenceIndex] ?? [];

        return blanks.map((blank, blankIndex) => {
          const expectedRaw = typeof blank.answer === 'string' ? blank.answer : '';
          const providedRaw = typeof provided?.[blankIndex] === 'string' ? provided[blankIndex] : '';
          const gradeFromBlank = (blank as any)?.grade as
            | 'perfect'
            | 'near'
            | 'partial'
            | 'wrong'
            | undefined;
          const reasonFromBlank = (blank as any)?.reason as string | undefined;

          const normalize = (value: string) => value.trim().toLocaleLowerCase();
          const expectedNormalized = normalize(expectedRaw);
          const providedNormalized = normalize(providedRaw);

          const isCorrect =
            expectedNormalized.length === 0
              ? providedNormalized.length === 0
              : expectedNormalized === providedNormalized;

          return {
            isCorrect,
            expected: expectedRaw,
            provided: providedRaw,
            correction: blank.correction,
            grade: gradeFromBlank ?? (isCorrect ? 'perfect' : 'wrong'),
            reason: reasonFromBlank,
          };
        });
      }),
    [sentences],
  );

  const evaluationsFromPayload = useMemo(() => {
    if (!lastEvaluationDetails) {
      return null;
    }

    return (lastEvaluationDetails.sentences ?? []).map((sentence: any) => {
      const blanks = Array.isArray(sentence?.blanks) ? sentence.blanks : [];
      return blanks.map((blank: any) => ({
        isCorrect: Boolean(blank?.isCorrect),
        expected: typeof blank?.expectedAnswer === 'string' ? blank.expectedAnswer : '',
        provided: typeof blank?.providedAnswer === 'string' ? blank.providedAnswer : '',
        correction: undefined,
        grade: blank?.grade as 'perfect' | 'near' | 'partial' | 'wrong' | undefined,
        reason: typeof blank?.reason === 'string' ? blank.reason : undefined,
      }));
    });
  }, [lastEvaluationDetails]);

  useEffect(() => {
    if (evaluationsFromPayload) {
      setAnswerEvaluations(evaluationsFromPayload);
    } else if (lastSubmission) {
      setAnswerEvaluations(computeEvaluations((lastSubmission.answers as string[][] | undefined) ?? []));
    } else {
      setAnswerEvaluations([]);
    }
  }, [computeEvaluations, evaluationsFromPayload, lastSubmission]);

  const handleAnswerChange = (sentenceIndex: number, blankIndex: number, value: string) => {
    if (isReadOnly) {
      return;
    }
    setAnswers((previous) => {
      const next = previous.map((sentenceAnswers) => [...sentenceAnswers]);
      if (!next[sentenceIndex]) {
        next[sentenceIndex] = [];
      }
      next[sentenceIndex][blankIndex] = value;
      return next;
    });
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!onExerciseSubmit) {
      return;
    }

    try {
      setIsSubmitting(true);
      await onExerciseSubmit(content, {
        type: ExerciseType.FillInBlank,
        answers,
      });
      // Evaluations update via useEffect when content.lastEvaluation changes after cache invalidation
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = answers.length > 0;

  return (
    <form
      onSubmit={handleSubmit}
      className={cn(
        'space-y-4',
        variant === 'card' && 'rounded-lg border border-border bg-card p-4 shadow-sm'
      )}
    >
      {content.title && (
        <h3 className="text-lg font-semibold">
          {renderInlineMarkdown(content.title, 'fill-in-blank-title')}
        </h3>
      )}
      <ContentDescription text={content.description} markdown />

      <div className="space-y-4">
        {sentences.map((sentence, sentenceIndex) => {
          const blanks = (sentence.blanks?.length
            ? sentence.blanks
            : [{ answer: (sentence as any)?.answer ?? '', correction: (sentence as any)?.correction }]) as Array<{
            blankId?: string;
            position?: number;
            answer: string;
            correction?: string;
            grade?: 'perfect' | 'near' | 'partial' | 'wrong';
          }>;
          const parts = (sentence.sentence ?? '').split(BLANK_MARKER);

          const renderBlank = (blank: NonNullable<typeof blanks[number]>, blankIndex: number, key: string) => {
            const evaluation = answerEvaluations[sentenceIndex]?.[blankIndex] as
              | { isCorrect: boolean; expected: string; provided: string; correction?: string; grade?: 'perfect' | 'near' | 'partial' | 'wrong'; reason?: string }
              | undefined;
            const grade = evaluation
              ? evaluation.grade ?? (evaluation.isCorrect ? 'perfect' : 'wrong')
              : null;
            const correctionMessage =
              evaluation?.reason ||
              (blank.correction?.trim()?.length ? blank.correction : '') ||
              (evaluation?.expected?.trim()?.length ? `Correct answer: ${evaluation.expected}` : 'Review the sentence and try again.');
            const theme =
              grade === 'perfect'
                ? {
                    border: 'border-emerald-300',
                    bg: 'bg-emerald-50/70',
                    text: 'text-emerald-900',
                    ring: 'border-emerald-300 bg-background text-emerald-600',
                    icon: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
                    title: 'Looks good',
                    iconType: 'success' as const,
                  }
                : grade === 'near' || grade === 'partial'
                  ? {
                      border: 'border-amber-300',
                      bg: 'bg-amber-50/70',
                      text: 'text-amber-900',
                      ring: 'border-amber-300 bg-background text-amber-700',
                      icon: 'bg-amber-100 text-amber-800 border border-amber-200',
                      title: grade === 'near' ? 'Almost there' : 'Partially correct',
                      iconType: 'warning' as const,
                    }
                  : grade === 'wrong'
                    ? {
                        border: 'border-destructive/50',
                        bg: 'bg-destructive/10',
                        text: 'text-destructive',
                        ring: 'border-destructive/50 bg-background text-destructive',
                        icon: 'bg-rose-100 text-rose-700 border border-rose-200',
                        title: 'Needs review',
                        iconType: 'error' as const,
                      }
                    : {
                        border: 'border-border',
                        bg: 'bg-background/60',
                        text: 'text-foreground',
                        ring: 'border-border bg-background text-muted-foreground',
                        icon: 'bg-muted text-muted-foreground border border-border/70',
                        title: 'Pending',
                        iconType: 'pending' as const,
                      };

            return (
              <div
                key={key}
                className={cn(
                  'flex items-center gap-2 rounded-md border px-2 py-1 transition',
                  theme.border,
                  theme.bg,
                  theme.text,
                )}
              >
                <Input
                  value={answers[sentenceIndex]?.[blankIndex] ?? ''}
                  onChange={(event) => handleAnswerChange(sentenceIndex, blankIndex, event.target.value)}
                  placeholder="Your answer"
                  className="w-40 min-w-[8rem] max-w-full"
                  readOnly={isReadOnly}
                />
                {grade && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className={cn(
                          'shrink-0 rounded-full border p-1.5 shadow-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
                          theme.ring,
                          theme.icon,
                        )}
                        aria-label="View feedback"
                      >
                        {theme.iconType === 'success' ? (
                          <CheckCircle2 className="h-4 w-4" />
                        ) : (
                          <AlertCircle className="h-4 w-4" />
                        )}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      align="end"
                      sideOffset={8}
                      className="w-64 space-y-2 text-sm leading-relaxed"
                    >
                      <p className="font-medium">{theme.title}</p>
                      <p className="text-muted-foreground">
                        {renderInlineMarkdown(
                          correctionMessage,
                          `${content.id}-blank-correction-${sentenceIndex}-${blankIndex}`,
                        )}
                      </p>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            );
          };

          return (
            <div
              key={sentence.sentenceId ?? `${content.id}-sentence-${sentenceIndex}`}
              className="space-y-3 rounded-md border border-border/80 bg-background/60 p-3"
            >
              <p className="text-sm font-medium text-muted-foreground">
                {/* Sentence {sentenceIndex + 1} */}
              </p>
              <div className="flex flex-wrap items-center gap-2 whitespace-pre-wrap text-base leading-relaxed">
                {parts.map((part, partIndex) => (
                  <Fragment key={`${sentenceIndex}-part-${partIndex}`}>
                    {part &&
                      renderInlineMarkdown(
                        part,
                        `${content.id}-sentence-${sentenceIndex}-part-${partIndex}`,
                      )}
                    {partIndex < blanks.length &&
                      renderBlank(
                        blanks[partIndex]!,
                        partIndex,
                        blanks[partIndex]?.blankId ?? `${sentenceIndex}-${partIndex}-${content.id}`,
                      )}
                  </Fragment>
                ))}
                {parts.length - 1 < blanks.length &&
                  blanks.slice(parts.length - 1).map((blank, blankIndex) => {
                    const blankPosition = (parts.length - 1) + blankIndex;
                    return renderBlank(
                      blank,
                      blankPosition,
                      blank.blankId ?? `${sentenceIndex}-tail-${blankIndex}`,
                    );
                  })}
              </div>
            </div>
          );
        })}
      </div>

      <ExerciseFeedbackList feedback={feedback ?? []} pending={isSubmitting} />
      {onExerciseSubmit && !isReadOnly && (
        <div className="flex justify-center pt-2">
          <Button
            type="submit"
            disabled={!canSubmit || isSubmitting}
            variant="submit"
            size="submit"
            isLoading={isSubmitting}
            loadingText="Submitting..."
            className="w-full sm:w-auto"
          >
            Submit answers
          </Button>
        </div>
      )}
    </form>
  );
}

